import Script from "next/script"
import cn from "classnames"
import CTABanner from "components/DynamicComponent/atoms/CTABanner"
import Meta from "components/Meta"
import PageHeaderWithImage from "components/DynamicComponent/molecules/PageHeaderWithImage"
import PageDivider from "components/DynamicComponent/atoms/PageDivider"
import TeamMember from "components/TeamMember"
import CallToAction from "components/CallToAction"
import Map from "components/Map"
import { getGlobals, getAllLocationPaths, getLocation } from "storyblok/api"
import { getPlainText } from "utilities/getPlainText"
import { getStoryblokLink } from "utilities/getStoryblokLink"
import getTelLink from "utilities/getTelLink"
import StoryblokVisualEditor from "components/StoryblokVisualEditor"
import richText from "utilities/richText"

export default function Location({ story }) {
  const description = `Get directions to our address, call us, or view a list of providers for our ${story?.content?.name} location. Don't forget to leave us a review!`

  const { twentysixty_seo } = story.content

  return (
    <>
      <Meta
        info={{
          title: twentysixty_seo?.title ? twentysixty_seo?.title : `${story?.content?.name} Location | Sonoran Spine`,
          og_title: twentysixty_seo?.title
            ? twentysixty_seo?.title
            : `${story?.content?.name} Location | Sonoran Spine`,
          twitter_title: twentysixty_seo?.title
            ? twentysixty_seo?.title
            : `${story?.content?.name} Location | Sonoran Spine`,
          description: twentysixty_seo?.description ? twentysixty_seo?.description : description,
          og_description: twentysixty_seo?.description ? twentysixty_seo?.description : description,
          twitter_description: twentysixty_seo?.description ? twentysixty_seo?.description : description,
          og_image: story?.content?.location_photo?.filename,
          twitter_image: story?.content?.location_photo?.filename,
        }}
      />

      <main>
        <StoryblokVisualEditor story={story?.content}>
          <PageHeaderWithImage
            blok={{
              heading: story?.content?.name,
              cta_label: "Review us on google",
              cta_link: story?.content?.reviewLink,
              image: story?.content?.photo,
            }}
          />

          <section className="bg-blue-light my-12">
            <div className="lg:flex">
              <div className="py-12 lg:py-24 px-8 lg:px-16 basis-1/3">
                <h2 className="uppercase text-m2 pb-2">Address</h2>
                <address className="not-italic uppercase font-primary pb-2">
                  {getPlainText(story?.content?.address, { addNewlines: false })}
                </address>
                <CallToAction href={getStoryblokLink(story?.content?.directionsLink)} style="map-pin">
                  Get Directions
                </CallToAction>
                <CallToAction href={getTelLink(story?.content?.phone)} style="phone">
                  {story?.content?.phone}
                </CallToAction>
                <div className="sm:columns-2 font-primary">{richText(story?.content?.hours)}</div>
                <div className="py-4">{richText(story?.content?.content)}</div>
              </div>
              <div className="basis-2/3 relative h-96 lg:h-auto">
                {story?.content?.coords ? (
                  <Map
                    key={story.content._uid}
                    lng={story?.content?.coords[0]}
                    lat={story?.content?.coords[1]}
                    initialZoom={14}
                    locations={[story?.content]}
                  />
                ) : null}
              </div>
            </div>
          </section>

          {story?.content?.teamMembers && story?.content?.teamMembers.length > 0 ? (
            <>
              <section className="pb-12 px-8">
                <div className="mx-auto max-w-screen-xl">
                  <div className="-mx-8">
                    <PageDivider blok={{ title: `${story?.content?.name} Providers` }} />
                  </div>
                  <div
                    className={cn("mx-auto", {
                      "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 sm:gap-8 justify-items-center":
                        story?.content?.teamMembers?.length > 1,
                    })}
                  >
                    {story?.content?.teamMembers?.map((member) => (
                      <TeamMember priority={true} {...member} key={member.name} />
                    ))}
                  </div>
                </div>
              </section>
            </>
          ) : null}
        </StoryblokVisualEditor>
      </main>
      <CTABanner />
      {story?.content?.code?.code && (
        <Script type="application/ld+json" id="location-schema">
          {story?.content?.code?.code}
        </Script>
      )}
    </>
  )
}

export async function getStaticProps({ params: { slug }, preview = null }) {
  const globals = await getGlobals()
  const story = await getLocation(slug, preview)

  return {
    props: {
      slug,
      globals: globals ?? null,
      story: story ?? null,
      preview,
    },
  }
}

export async function getStaticPaths() {
  return {
    paths: await getAllLocationPaths(),
    fallback: false,
  }
}
